import { batteryConcertErrorCode as concertErrorCode } from "~/api/battery-concert-application-errors"
import type { SolarConcertOffersDto } from "~/api/dto/solar-concert.dto"
import useCalculateSolarProposalPricing from "~/api/hooks/useCalculateSolarProposalPricing"
import useGenerateSolarConcertLoanDocuments from "~/api/hooks/useGenerateSolarConcertLoanDocuments"
import useInitiateSolarConcertApplication from "~/api/hooks/useInitiateSolarConcertApplication"
import useSaveSolarConcertOffer from "~/api/hooks/useSaveSolarConcertOffer"
import useSendSolarConcertLoanDocuments from "~/api/hooks/useSendSolarConcertLoanDocuments"
import useSolarConcertApplication from "~/api/hooks/useSolarConcertApplication"
import useSolarConcertOffers from "~/api/hooks/useSolarConcertOffers"
import useSubmitSolarConcertApplication from "~/api/hooks/useSubmitSolarConcertApplication"
import useSubmitSolarConcertDocuments from "~/api/hooks/useSubmitSolarConcertDocuments"
import useUploadSolarConcertDocument from "~/api/hooks/useUploadSolarConcertDocument"
import {
  isSolarRefreshPricingRequiredError,
  SOLAR_CONCERT_AMOUNT_OUT_OF_RANGE_CODE,
  SOLAR_CONCERT_OFFER_UNAVAILABLE_CODE,
  SOLAR_CONCERT_PRICING_REQUIRED_CODE,
  SOLAR_FINANCING_LOCKED_CODE,
  solarProposalErrorCode,
  solarProposalErrorMessage,
} from "~/api/solar-proposal-errors"

import { solarPricingErrorMessage } from "./solar-pricing-error"
import { SolarRefreshPricingFix } from "./SolarRefreshPricingFix"
import { concertAmountBlockedPrompt } from "../ProposalSetup/financing/concert/concert-amount-copy"
import type {
  ConcertApplicationBundle,
  ConcertOfferPickerBundle,
} from "../ProposalSetup/financing/concert/concert-kind-bundle"
import { CONCERT_SUBMIT_FAILED_MESSAGE } from "../ProposalSetup/financing/concert/ConcertApplicationForm"

export { SOLAR_CONCERT_FINANCE_TYPE } from "./solar-finance-partner"

export const SAVE_SOLAR_CONCERT_OFFER_LABEL = `Save Loan Product`
export const SOLAR_CONCERT_OFFER_SAVED_LABEL = `Loan Product saved and payment quoted`
export const SOLAR_CONCERT_OFFER_SAVED_WITHOUT_PAYMENT_LABEL = `Loan Product saved. Refresh Pricing to quote its payment.`
export const SOLAR_CONCERT_PRICING_REQUIRED_PROMPT = `Refresh Pricing before choosing a Concert offer. Offers are only shown for the current price.`
export const SOLAR_CONCERT_UNAVAILABLE_PROMPT = `Concert offers are not available for this Solar Proposal.`
export const SOLAR_CONCERT_EMPTY_PROMPT = `Concert returned no offers for this price.`
export const SOLAR_CONCERT_SELECTED_OFFER_UNAVAILABLE_PROMPT = `Concert no longer returns the saved offer. Choose another to quote a payment.`
export const SOLAR_CONCERT_AMOUNT_FALLBACK_PROMPT = `This proposal's price is outside the Concert amount range.`
export const SOLAR_CONCERT_SAVE_ERROR_PROMPT = `Unable to save this offer. Your selection is still here.`
export const SOLAR_CONCERT_READ_ONLY_PROMPT = `You cannot change the Loan Product for this Solar Proposal.`

/** The server names the bound and the price; these say what to do instead. */
const SAVE_REFUSALS_WITH_MESSAGE: ReadonlySet<string> = new Set([
  SOLAR_CONCERT_AMOUNT_OUT_OF_RANGE_CODE,
  SOLAR_CONCERT_OFFER_UNAVAILABLE_CODE,
  SOLAR_CONCERT_PRICING_REQUIRED_CODE,
  SOLAR_FINANCING_LOCKED_CODE,
  `SOLAR_CONCERT_NOT_OFFERED`,
])

/**
 * Solar Proposal reads and commands for the shared Concert offer picker.
 * Refresh Pricing stores the offers for the new price; saving one writes its
 * Quote with Concert's payment and never calls PowurOS.
 */
export const solarConcertOfferBundle: ConcertOfferPickerBundle<SolarConcertOffersDto> =
  {
    useOffers: useSolarConcertOffers,
    useSave: useSaveSolarConcertOffer,
    // The save answers with the stored offers; a saved offer has its Quote.
    savedOutcome: (offers) =>
      offers.selectedOffer != null && offers.status === `available`
        ? `quoted`
        : `saved_only`,
    useRefreshPricing: useCalculateSolarProposalPricing,
    describeSaveError: (error) => {
      const code = solarProposalErrorCode(error)
      return (
        (code != null && SAVE_REFUSALS_WITH_MESSAGE.has(code)
          ? solarProposalErrorMessage(error)
          : null) ?? SOLAR_CONCERT_SAVE_ERROR_PROMPT
      )
    },
    describeRefreshError: solarPricingErrorMessage,
    amountBlockedPrompt: (eligibility) =>
      concertAmountBlockedPrompt(eligibility, `Concert financing`),
    copy: {
      saveLabel: SAVE_SOLAR_CONCERT_OFFER_LABEL,
      saveBusyLabel: `Saving`,
      savedLabel: SOLAR_CONCERT_OFFER_SAVED_LABEL,
      savedWithoutPaymentLabel: SOLAR_CONCERT_OFFER_SAVED_WITHOUT_PAYMENT_LABEL,
      pricingRequired: SOLAR_CONCERT_PRICING_REQUIRED_PROMPT,
      unavailable: SOLAR_CONCERT_UNAVAILABLE_PROMPT,
      empty: SOLAR_CONCERT_EMPTY_PROMPT,
      selectedOfferUnavailable: SOLAR_CONCERT_SELECTED_OFFER_UNAVAILABLE_PROMPT,
      amountIneligibleFallback: SOLAR_CONCERT_AMOUNT_FALLBACK_PROMPT,
      readOnly: SOLAR_CONCERT_READ_ONLY_PROMPT,
    },
  }

export const SOLAR_CONCERT_REFRESH_REQUIRED_PROMPT = `Pricing changed since this Quote was priced. Refresh Pricing, then submit again.`
export const SOLAR_CONCERT_SETUP_REQUIRED_PROMPT = `Customer setup is required. Close this form and apply again.`

/**
 * Why a Concert Initiate or submit was refused: the seller gate and other
 * refusals carry the server's reason, a stale Quote says to refresh.
 */
export const solarConcertCommandErrorMessage = (error: unknown): string => {
  if (isSolarRefreshPricingRequiredError(error)) {
    return SOLAR_CONCERT_REFRESH_REQUIRED_PROMPT
  }
  if (concertErrorCode(error) === `CONCERT_SETUP_REQUIRED`) {
    return SOLAR_CONCERT_SETUP_REQUIRED_PROMPT
  }
  // The seller gate (SOLAR_CONCERT_SELLER_NOT_READY) and every other refusal
  // (SOLAR_CONCERT_APPLICATION_REFUSED) say what to fix in their message.
  return solarProposalErrorMessage(error) ?? CONCERT_SUBMIT_FAILED_MESSAGE
}

/**
 * Solar Proposal Concert application commands for the shared status and
 * form. Supporting documents are on: Solar's stipulations are uploaded here,
 * as V1's were. The supplemental package lives in the Contract drawer.
 */
export const solarConcertApplicationBundle: ConcertApplicationBundle = {
  testIdPrefix: `solar`,
  useStatus: useSolarConcertApplication,
  useInitiate: useInitiateSolarConcertApplication,
  useSubmit: useSubmitSolarConcertApplication,
  useUpload: useUploadSolarConcertDocument,
  useSubmitDocuments: useSubmitSolarConcertDocuments,
  useGenerateLoanDocuments: useGenerateSolarConcertLoanDocuments,
  useSendLoanDocuments: useSendSolarConcertLoanDocuments,
  // The seller chooses email or in-person signing for Concert's loan documents.
  inPersonLoanDocuments: true,
  supportingDocumentActions: true,
  Supplemental: null,
  isRefreshRequiredError: isSolarRefreshPricingRequiredError,
  RefreshPricing: SolarRefreshPricingFix,
  describeSubmitError: solarConcertCommandErrorMessage,
  copy: {
    offerNoun: `Solar`,
    noApplication: `No Existing Loan Application yet.`,
    setupFailed: `Concert customer setup failed. Contact support to correct the customer details, then apply again.`,
    setupUnconfirmed: `Concert customer setup is not confirmed. Contact support before applying again.`,
  },
}
